import React, { useState, useCallback } from "react";
import { useSoundLibrary } from "../../soundLibrary";
import { Note } from "../../types/sound";
import styles from "../../styles/UntrainedVsTrainedDemo.module.css";

/**
 * UntrainedVsTrainedDemo
 *
 * Demonstrates the difference between what an untrained ear hears vs a trained ear
 * using the iconic "Smoke on the Water" riff by Deep Purple.
 */

// Tempo calculations from MIDI timing
const EIGHTH_NOTE = 495;
const QUARTER_NOTE = 990;
const STACCATO_EIGHTH = 247;
const REST_AFTER_PHRASE_1 = 288;
const REST_AFTER_PHRASE_2 = 54;

// Trained version: Full accurate riff
const TRAINED_RIFF: Note[] = [
    'G3', 'A#3', 'C4',
    'G3', 'A#3', 'C#4', 'C4',
    'G3', 'A#3', 'C4', 'A#3', 'G3'
];

const TRAINED_DURATIONS = [
    EIGHTH_NOTE, EIGHTH_NOTE, EIGHTH_NOTE,
    STACCATO_EIGHTH, EIGHTH_NOTE, STACCATO_EIGHTH, QUARTER_NOTE,
    EIGHTH_NOTE, EIGHTH_NOTE, 742, EIGHTH_NOTE, 1734
];

// Untrained version: Misses details
const UNTRAINED_RIFF: Note[] = [
    'G3', 'A#3', 'C4',
    'G3', 'A#3', 'C4', 'C4',
    'G3', 'A#3', 'C4'
];

const UNTRAINED_DURATIONS = [
    EIGHTH_NOTE, EIGHTH_NOTE, EIGHTH_NOTE,
    STACCATO_EIGHTH, EIGHTH_NOTE, STACCATO_EIGHTH, QUARTER_NOTE,
    EIGHTH_NOTE, EIGHTH_NOTE, EIGHTH_NOTE
];

interface UntrainedVsTrainedDemoProps {
    className?: string;
}

export default function UntrainedVsTrainedDemo({ className }: UntrainedVsTrainedDemoProps) {
    const { playNote, loadInstrument, stopAll } = useSoundLibrary();
    const [playingUntrained, setPlayingUntrained] = useState(false);
    const [playingTrained, setPlayingTrained] = useState(false);

    const playRiff = useCallback(async (
        notes: Note[],
        durations: number[],
        velocity: number
    ) => {
        for (let i = 0; i < notes.length; i++) {
            playNote('saw-synth', notes[i], {
                duration: durations[i] / 1000,
                velocity: velocity
            });
            await new Promise(resolve => setTimeout(resolve, durations[i]));

            if (i === 2) {
                await new Promise(resolve => setTimeout(resolve, REST_AFTER_PHRASE_1));
            } else if (i === 6) {
                await new Promise(resolve => setTimeout(resolve, REST_AFTER_PHRASE_2));
            }
        }
    }, [playNote]);

    const playUntrained = useCallback(async () => {
        if (playingUntrained) {
            stopAll();
            setPlayingUntrained(false);
            return;
        }
        setPlayingUntrained(true);
        await loadInstrument('saw-synth');
        await playRiff(UNTRAINED_RIFF, UNTRAINED_DURATIONS, 0.7);
        setPlayingUntrained(false);
    }, [playingUntrained, playRiff, loadInstrument, stopAll]);

    const playTrained = useCallback(async () => {
        if (playingTrained) {
            stopAll();
            setPlayingTrained(false);
            return;
        }
        setPlayingTrained(true);
        await loadInstrument('saw-synth');
        await playRiff(TRAINED_RIFF, TRAINED_DURATIONS, 0.75);
        setPlayingTrained(false);
    }, [playingTrained, playRiff, loadInstrument, stopAll]);

    return (
        <div className={`${styles.container} ${className || ''}`}>
            <div className={styles.header}>
                <span className={styles.riffName}>Smoke on the Water</span>
                <span className={styles.riffArtist}>Deep Purple</span>
            </div>

            <div className={styles.comparison}>
                <div className={`${styles.side} ${styles.untrained}`}>
                    <div className={styles.sideHeader}>
                        <span className={styles.label}>Untrained Ear</span>
                        <span className={styles.description}>Hears the main notes</span>
                    </div>
                    <div className={styles.notes}>
                        <span className={styles.note}>G</span>
                        <span className={styles.arrow}>→</span>
                        <span className={styles.note}>B♭</span>
                        <span className={styles.arrow}>→</span>
                        <span className={styles.note}>C</span>
                        <span className={styles.arrow}>→</span>
                        <span className={styles.note}>G</span>
                        <span className={styles.arrow}>→</span>
                        <span className={styles.note}>B♭</span>
                        <span className={styles.arrow}>→</span>
                        <span className={styles.note}>C</span>
                        <span className={styles.fadeOut}>→ ?</span>
                    </div>
                    <p className={styles.caption}>You recognize the riff but the second half gets fuzzy—can you hum how it ends?</p>
                    <button
                        onClick={playUntrained}
                        className={`${styles.playButton} ${playingUntrained ? styles.playing : ''}`}
                    >
                        {playingUntrained ? '⏹ Stop' : '▶ Play'}
                    </button>
                </div>

                <div className={styles.divider}>
                    <span className={styles.vs}>vs</span>
                </div>

                <div className={`${styles.side} ${styles.trained}`}>
                    <div className={styles.sideHeader}>
                        <span className={styles.label}>Trained Ear</span>
                        <span className={styles.description}>Hears the complete riff</span>
                    </div>
                    <div className={styles.notes}>
                        <span className={styles.note}>G</span>
                        <span className={styles.arrow}>→</span>
                        <span className={styles.note}>B♭</span>
                        <span className={styles.arrow}>→</span>
                        <span className={styles.note}>C</span>
                        <span className={styles.arrow}>→</span>
                        <span className={styles.note}>G</span>
                        <span className={styles.arrow}>→</span>
                        <span className={styles.note}>B♭</span>
                        <span className={styles.arrow}>→</span>
                        <span className={`${styles.note} ${styles.highlight}`}>C♯</span>
                        <span className={styles.arrow}>→</span>
                        <span className={styles.note}>C</span>
                        <span className={styles.arrow}>→</span>
                        <span className={styles.note}>B♭</span>
                        <span className={styles.arrow}>→</span>
                        <span className={styles.note}>G</span>
                    </div>
                    <p className={styles.caption}>You hear every note—including the clever C♯→C move that makes it satisfying</p>
                    <button
                        onClick={playTrained}
                        className={`${styles.playButton} ${playingTrained ? styles.playing : ''}`}
                    >
                        {playingTrained ? '⏹ Stop' : '▶ Play'}
                    </button>
                </div>
            </div>
        </div>
    );
}
